import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import App from "./App";
import { ApiError } from "./lib/api";
import { AuthProvider } from "./lib/auth";

import "./styles/tokens.css";
import "./styles/app.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Field conditions: a request that fails because there is no signal
      // should not hammer the radio. Auth failures must never be retried —
      // the session is gone and retrying only delays the login screen.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && (error.isAuthFailure || error.isBusinessRule)) return false;
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    },
    mutations: { retry: false },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <App />
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </StrictMode>,
);

// Service worker: caches the app shell so Keystone opens without a network.
// It deliberately does not cache API responses — showing yesterday's job list
// as though it were today's is worse than showing nothing. Real offline data
// is MOBILE-2.
// Not on the customer's quote page (/q/...). A stranger who opened a link from
// an email did not ask to have a trade's field app installed on their phone,
// and a worker registered from that page would keep serving the shell from
// their cache long after the quote was decided. They get an ordinary web page.
const isCustomerPage = window.location.pathname.startsWith("/q/");

if ("serviceWorker" in navigator && import.meta.env.PROD && !isCustomerPage) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      /* a failed registration costs offline shell caching, nothing else */
    });
  });
}
